import React, { useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton,
  Chip,
  Alert,
  SelectChangeEvent,
} from "@mui/material";
import {
  CreateSubscriptionPlanRequest,
  SubscriptionPlan,
  useCreateSubscriptionPlanMutation,
  useDeleteSubscriptionPlanMutation,
  useGetSubscriptionPlansQuery,
  useUpdateSubscriptionPlanMutation,
  CreateReferralBonusRuleRequest,
  useGetReferralBonusRulesQuery,
  useCreateReferralBonusRuleMutation,
  useDeleteReferralBonusRuleMutation,
  RetentionReportParams,
  useLazyGetSubscriptionRetentionReportQuery,
} from "../../../entities/admin-subscriptions";
import { ResidentCaliber } from "../../../entities/admin-events";
import { format } from "date-fns";
import { LoadingIndicator } from "../../../shared/ui/loading-indicator";
import {
  renderCaliberName,
  getCalibers,
  getCaliberColor,
} from "../../../shared/lib/caliber";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";

const SubscriptionPlansManagement = () => {
  // States for dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<SubscriptionPlan | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // States for referral bonus rules
  const [openRuleDialog, setOpenRuleDialog] = useState(false);
  const [confirmDeleteRule, setConfirmDeleteRule] = useState<number | null>(
    null
  );
  const [ruleValidationError, setRuleValidationError] = useState<string | null>(
    null
  );

  // Form state
  const [formData, setFormData] = useState<CreateSubscriptionPlanRequest>({
    title: "",
    description: "",
    price: "",
    currencyId: 398,
    intervalPeriod: "M",
    intervalCount: 1,
    isActive: true,
    rang: "",
    allowedCaliber: undefined,
  });

  // Form state for referral bonus rules
  const [ruleFormData, setRuleFormData] =
    useState<CreateReferralBonusRuleRequest>({
      caliber: ResidentCaliber.VOSHOD,
      bonusDays: 10,
    });

  // State for retention report
  const [reportParams] = useState<RetentionReportParams>({});

  // RTK Query hooks
  const { data, isLoading } = useGetSubscriptionPlansQuery();
  const [createPlan, { isLoading: isCreatingPlan }] =
    useCreateSubscriptionPlanMutation();
  const [updatePlan, { isLoading: isUpdatingPlan }] =
    useUpdateSubscriptionPlanMutation();
  const [deletePlan, { isLoading: isDeletingPlan }] =
    useDeleteSubscriptionPlanMutation();

  // RTK Query hooks for referral bonus rules
  const { data: rulesData, isLoading: rulesLoading } =
    useGetReferralBonusRulesQuery();
  const [createRule, { isLoading: isCreatingRule }] =
    useCreateReferralBonusRuleMutation();
  const [deleteRule, { isLoading: isDeletingRule }] =
    useDeleteReferralBonusRuleMutation();

  // RTK Query hook for retention report
  const [getSubscriptionRetentionReport, { isLoading: isReportLoading }] =
    useLazyGetSubscriptionRetentionReportQuery();

  // Handle dialog open for creating new plan
  const handleCreateDialogOpen = () => {
    setFormData({
      title: "",
      description: "",
      price: "",
      currencyId: 398,
      intervalPeriod: "M",
      intervalCount: 1,
      isActive: true,
      rang: "",
      allowedCaliber: undefined,
    });
    setValidationError(null);
    setEditMode(false);
    setOpenDialog(true);
  };

  // Handle dialog open for editing plan
  const handleEditDialogOpen = (plan: SubscriptionPlan) => {
    setCurrentPlan(plan);
    setFormData({
      title: plan.name,
      description: plan.description,
      price: plan.price.value.toString(),
      currencyId: 398,
      intervalPeriod: plan.intervalPeriod,
      intervalCount: plan.intervalCount,
      isActive: plan.isActive,
      rang: plan.rang.toString(),
      allowedCaliber: plan.allowedCaliber,
    });
    setValidationError(null);
    setEditMode(true);
    setOpenDialog(true);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setOpenDialog(false);
    setCurrentPlan(null);
    setValidationError(null);
  };

  // Handle form input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    // Для числовых полей позволяем пустые значения
    if (type === "number") {
      setFormData({
        ...formData,
        [name]: value === "" ? "" : Number(value),
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === "checkbox" ? checked : value,
      });
    }
  };

  // Handle select change
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;

    // Для числовых полей преобразуем значения в числа
    if (name === "currencyId") {
      setFormData({
        ...formData,
        [name]: Number(value),
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  // Handle caliber select change
  const handleCaliberChange = (e: SelectChangeEvent<string>) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      allowedCaliber: value === "" ? undefined : (value as ResidentCaliber),
    });
  };

  // Validate form
  const validateForm = (): boolean => {
    if (!formData.title.trim()) {
      setValidationError("Название обязательно для заполнения");
      return false;
    }

    if (
      formData.price === "" ||
      (typeof formData.price === "number" && formData.price <= 0)
    ) {
      setValidationError("Цена должна быть положительным числом");
      return false;
    }

    if (formData.intervalCount <= 0) {
      setValidationError(
        "Количество периодов должно быть положительным числом"
      );
      return false;
    }

    if (!formData.intervalPeriod) {
      setValidationError("Период обязателен для заполнения");
      return false;
    }

    return true;
  };

  // Handle form submit
  const handleSubmit = async () => {
    setValidationError(null);

    if (!validateForm()) {
      return;
    }

    try {
      // Преобразуем строковые значения в числа для API
      const apiData = {
        ...formData,
        price:
          typeof formData.price === "string"
            ? formData.price === ""
              ? 0
              : Number(formData.price)
            : formData.price,
        rang:
          typeof formData.rang === "string"
            ? formData.rang === ""
              ? 0
              : Number(formData.rang)
            : formData.rang,
      };

      if (editMode && currentPlan) {
        await updatePlan({ id: currentPlan.id, data: apiData });
      } else {
        await createPlan(apiData);
      }
      handleDialogClose();
    } catch (error) {
      console.error("Error saving plan:", error);
      setValidationError("Ошибка при сохранении плана. Попробуйте еще раз.");
    }
  };

  // Handle delete plan
  const handleDelete = async (id: number) => {
    try {
      await deletePlan(id);
      setConfirmDelete(null);
    } catch (error) {
      console.error("Error deleting plan:", error);
    }
  };

  // Handle referral bonus rule dialog open
  const handleRuleDialogOpen = () => {
    setRuleValidationError(null);
    setOpenRuleDialog(true);
  };

  // Handle referral bonus rule dialog close
  const handleRuleDialogClose = () => {
    setOpenRuleDialog(false);
    setRuleValidationError(null);
  };

  // Handle rule form input change
  const handleRuleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRuleFormData({
      ...ruleFormData,
      [name]: name === "bonusDays" ? value : "",
    });
  };

  // Handle rule caliber select change
  const handleRuleCaliberChange = (e: SelectChangeEvent<string>) => {
    const { value } = e.target;
    setRuleFormData({
      ...ruleFormData,
      caliber: value as ResidentCaliber,
    });
  };

  // Validate rule form
  const validateRuleForm = (): boolean => {
    if (ruleFormData.bonusDays <= 0) {
      setRuleValidationError("Количество дней должно быть больше нуля");
      return false;
    }

    // Check if rule for this caliber already exists
    const existingRule = rulesData?.rules.find(
      (rule) => rule.caliber === ruleFormData.caliber
    );
    if (existingRule) {
      setRuleValidationError(
        "Правило для данной категории резидента уже существует"
      );
      return false;
    }

    return true;
  };

  // Handle rule form submit
  const handleRuleSubmit = async () => {
    setRuleValidationError(null);

    if (!validateRuleForm()) {
      return;
    }

    try {
      await createRule(ruleFormData);
      handleRuleDialogClose();
    } catch (error) {
      console.error("Error creating rule:", error);
      setRuleValidationError(
        "Ошибка при создании правила. Попробуйте еще раз."
      );
    }
  };

  // Handle delete referral bonus rule
  const handleDeleteRule = async (id: number) => {
    try {
      await deleteRule(id);
      setConfirmDeleteRule(null);
    } catch (error) {
      console.error("Error deleting rule:", error);
    }
  };

  // Handle download retention report
  const handleDownloadReport = async () => {
    const result = await getSubscriptionRetentionReport(reportParams).unwrap();
    // Create download link
    const url = window.URL.createObjectURL(result);
    const link = document.createElement("a");
    link.href = url;
    link.download = `community-users-${
      new Date().toISOString().split("T")[0]
    }.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Render interval period in Russian
  const renderIntervalPeriod = (period: string, count: number) => {
    let periodName = "";
    switch (period) {
      case "D":
        periodName = count === 1 ? "день" : count < 5 ? "дня" : "дней";
        break;
      case "W":
        periodName = count === 1 ? "неделя" : count < 5 ? "недели" : "недель";
        break;
      case "M":
        periodName = count === 1 ? "месяц" : count < 5 ? "месяца" : "месяцев";
        break;
      case "Q":
        periodName =
          count === 1 ? "квартал" : count < 5 ? "квартала" : "кварталов";
        break;
      case "Y":
        periodName = count === 1 ? "год" : count < 5 ? "года" : "лет";
        break;
      default:
        periodName = "период";
    }
    return `${count} ${periodName}`;
  };

  if (isLoading || rulesLoading) {
    return <LoadingIndicator />;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography fontWeight={600} fontSize={24} mb={2}>
        Управление планами подписок
      </Typography>

      {/* Header with Add Button and Download Report Button */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadReport}
          disabled={isReportLoading}
        >
          {isReportLoading ? "Загрузка..." : "Скачать отчёт по удержанию"}
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateDialogOpen}
        >
          Добавить план
        </Button>
      </Box>

      {/* Plans Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: "secondary.main" }}>
            <TableRow>
              <TableCell>Название</TableCell>
              <TableCell>Описание</TableCell>
              <TableCell>Цена</TableCell>
              <TableCell>Период</TableCell>
              <TableCell>Доступно для</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Дата создания</TableCell>
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.plans?.map((plan) => (
              <TableRow key={plan.id}>
                <TableCell>{plan.name}</TableCell>
                <TableCell>{plan.description}</TableCell>
                <TableCell>
                  {plan.price.value} {plan.price.unicode}
                </TableCell>
                <TableCell>
                  {renderIntervalPeriod(
                    plan.intervalPeriod,
                    plan.intervalCount
                  )}
                </TableCell>
                <TableCell>
                  {plan.allowedCaliber ? (
                    <Chip
                      label={renderCaliberName(plan.allowedCaliber)}
                      color={getCaliberColor(plan.allowedCaliber)}
                      size="small"
                    />
                  ) : (
                    <Chip label="Все уровни" color="default" size="small" />
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={plan.isActive ? "Активен" : "Не активен"}
                    color={plan.isActive ? "success" : "default"}
                  />
                </TableCell>
                <TableCell>
                  {format(new Date(plan.createdAt), "dd.MM.yyyy")}
                </TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => handleEditDialogOpen(plan)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => setConfirmDelete(plan.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Referral Bonus Rules Section */}
      <Typography fontWeight={600} fontSize={24} mt={4} mb={2}>
        Правила начисления бонусных дней
      </Typography>

      {/* Header with Add Rule Button */}
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleRuleDialogOpen}
        >
          Добавить правило
        </Button>
      </Box>

      {/* Rules Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: "secondary.main" }}>
            <TableRow>
              <TableCell>Категория резидента</TableCell>
              <TableCell>Количество бонусных дней</TableCell>
              <TableCell>Дата создания</TableCell>
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rulesData?.rules?.map((rule) => (
              <TableRow key={rule.id}>
                <TableCell>
                  <Chip
                    label={renderCaliberName(rule.caliber)}
                    color={getCaliberColor(rule.caliber)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{rule.bonusDays} дней</TableCell>
                <TableCell>
                  {format(new Date(rule.createdAt), "dd.MM.yyyy")}
                </TableCell>
                <TableCell>
                  <IconButton
                    color="error"
                    onClick={() => setConfirmDeleteRule(rule.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {(!rulesData?.rules || rulesData.rules.length === 0) && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="text.secondary">
                    Правила не найдены
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        {isCreatingPlan || (isUpdatingPlan && <LoadingIndicator />)}
        <DialogTitle
          textTransform="none"
          fontWeight={600}
          fontSize={20}
          textAlign="center"
        >
          {editMode ? "Редактировать план" : "Добавить новый план"}
        </DialogTitle>
        <DialogContent>
          {validationError && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {validationError}
            </Alert>
          )}

          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              name="title"
              label="Название"
              value={formData.title}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              name="description"
              label="Описание"
              value={formData.description}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={3}
            />
            <Box display="flex" gap={2}>
              <TextField
                name="price"
                label="Цена"
                type="number"
                value={formData.price}
                onChange={handleInputChange}
                fullWidth
                required
                disabled={editMode && currentPlan?.id !== undefined}
              />
              <FormControl fullWidth>
                <InputLabel>Валюта</InputLabel>
                <Select
                  name="currencyId"
                  value={formData.currencyId.toString()}
                  onChange={handleSelectChange}
                  disabled={editMode && currentPlan?.id !== undefined}
                >
                  <MenuItem value={"398"}>₸ (Тенге)</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box display="flex" gap={2}>
              <FormControl fullWidth>
                <InputLabel>Период</InputLabel>
                <Select
                  name="intervalPeriod"
                  value={formData.intervalPeriod}
                  onChange={handleSelectChange}
                  disabled={editMode && currentPlan?.id !== undefined}
                >
                  <MenuItem value="D">День</MenuItem>
                  <MenuItem value="W">Неделя</MenuItem>
                  <MenuItem value="M">Месяц</MenuItem>
                  <MenuItem value="Q">Квартал</MenuItem>
                  <MenuItem value="Y">Год</MenuItem>
                </Select>
              </FormControl>
              <TextField
                name="intervalCount"
                label="Количество периодов"
                type="number"
                value={formData.intervalCount}
                onChange={handleInputChange}
                fullWidth
                required
                disabled={editMode && currentPlan?.id !== undefined}
              />
            </Box>
            <Box display="flex" gap={2}>
              <FormControlLabel
                control={
                  <Switch
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                }
                label="Активен"
              />
              <TextField
                name="rang"
                label="Ранг (порядок сортировки)"
                type="number"
                value={formData.rang}
                onChange={handleInputChange}
                fullWidth
              />
            </Box>
            <FormControl fullWidth>
              <InputLabel>Доступно для уровня</InputLabel>
              <Select
                name="allowedCaliber"
                value={formData.allowedCaliber || "Все уровни"}
                onChange={handleCaliberChange}
              >
                <MenuItem value="Все уровни">Все уровни</MenuItem>
                {getCalibers().map((caliber) => (
                  <MenuItem key={caliber.value} value={caliber.value}>
                    {caliber.fullLabel}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Отмена</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editMode ? "Сохранить" : "Создать"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
      >
        {isDeletingPlan && <LoadingIndicator />}
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <Typography>
            Вы действительно хотите удалить этот план подписки?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Отмена</Button>
          <Button
            onClick={() => confirmDelete && handleDelete(confirmDelete)}
            variant="contained"
            color="error"
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Rule Dialog */}
      <Dialog
        open={openRuleDialog}
        onClose={handleRuleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        {isCreatingRule && <LoadingIndicator />}
        <DialogTitle
          textTransform="none"
          fontWeight={600}
          fontSize={20}
          textAlign="center"
        >
          Добавить правило начисления бонусных дней
        </DialogTitle>
        <DialogContent>
          {ruleValidationError && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {ruleValidationError}
            </Alert>
          )}

          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <FormControl fullWidth>
              <InputLabel>Категория резидента</InputLabel>
              <Select
                name="caliber"
                value={ruleFormData.caliber}
                onChange={handleRuleCaliberChange}
              >
                {getCalibers().map((caliber) => (
                  <MenuItem key={caliber.value} value={caliber.value}>
                    {caliber.fullLabel}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              name="bonusDays"
              label="Количество бонусных дней"
              type="number"
              value={ruleFormData.bonusDays}
              onChange={handleRuleInputChange}
              fullWidth
              required
              helperText="Количество дней должно быть больше нуля"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRuleDialogClose}>Отмена</Button>
          <Button
            onClick={handleRuleSubmit}
            variant="contained"
            color="primary"
          >
            Создать
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Rule Confirmation Dialog */}
      <Dialog
        open={confirmDeleteRule !== null}
        onClose={() => setConfirmDeleteRule(null)}
      >
        {isDeletingRule && <LoadingIndicator />}
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <Typography>
            Вы действительно хотите удалить это правило начисления бонусных
            дней?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteRule(null)}>Отмена</Button>
          <Button
            onClick={() =>
              confirmDeleteRule && handleDeleteRule(confirmDeleteRule)
            }
            variant="contained"
            color="error"
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionPlansManagement;
